<template>
    <div class="col-12 mx-auto">
        <page-title :title="$t('registers.lecturers.title')" :showTitle="false"></page-title>
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">
                    {{ $t('registers.lecturers.title') }}
                </div>
            </div>

            <div class="card-header" v-if="showSettingsBtn">
                <button type="button" class="btn btn-primary btn-lg"
                        v-if="showNewButton && claims.lecturer.create"
                        @click="goToCreate">
                    {{$t('buttons.create.btnText')}}
                </button>


                <button type="button" class="btn btn-primary btn-lg"
                        v-if="showExportTemplateButton"
                        @click="goToExportTemplate">
                    {{$t('buttons.exportTemplate.btnText')}}
                </button>
                <label>
                    <input type="checkbox" id="exportWithData" class="checkbox" v-if="showExportTemplateButton">
                    <span v-if="showExportTemplateButton">{{$t('checkboxes.exportWithData.cbText')}}</span>
                </label>

                <form enctype="multipart/form-data" method="post" id="importForm" class="inlineForm">
                    <div class="inlineForm">
                        <button type="button" class="btn btn-primary btn-lg"
                                v-if="showImportFromExcelButton"
                                @click="selectImportFiles">
                            {{$t('buttons.importFromExcel.btnText')}}
                        </button>
                        <input type="file" id="importFile" class="selectFile" @change="goToImportFromExcel" />
                    </div>
                </form>


                <i class="fas fa-cogs pointer fa-2x float-right text-success"
                   v-if="showSettingsBtn"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @deleteGridSettings="deleteGridSettings"
                                    @userSettingsChanged="userSettingsChanged">
                </grid-user-settings>
            </div>

            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-0 mb-2" role="toolbar">
                    <div class="form-inline col-6">

                    </div>

                    <div class="input-group">
                        <input v-model="searchQuery" type="text"
                               v-on:keyup.enter.stop="massSearch"
                               v-on:keyup.esc.stop="clearFilters"
                               class="form-control"
                               aria-label="Mass search input"
                               aria-describedby="massSearch">
                        <button class="btn btn-secondary" @click.stop="massSearch">
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button class="btn btn-secondary"
                                @click.stop="clearFilters">
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size='defaultGridRows'
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError">
                </kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource1'"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :toolbar="toolbar"
                            :excel="gridOptions.excel"
                            :dataBound="dataBound">
                    <kendo-grid-column :field="'id'"
                                       :filterable="false"
                                       :width="100"
                                       :lockable="false"
                                       :locked="false"
                                       :hidden="true"
                                       :title="$t('registers.lectors.id')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'fullName'"
                                       :width="300"
                                       :lockable="true"
                                       :locked="true"
                                       :hidden="isEnVersion"
                                       :title="$t('person.firstName')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'fullNameEn'"
                                       :width="300"
                                       :lockable="true"
                                       :locked="true"
                                       :sortable="false"
                                       :filterable="false"
                                       :title="$t('person.gridHeaders.nameTransliterated')">
                    </kendo-grid-column>
                    <kendo-grid-column v-if="showBtns"
                                       :template="btnsTemplate"
                                       field="btnsColumn"
                                       :title="$t('actions')"
                                       :sortable="false"
                                       :filterable="false"
                                       :width="150"
                                       :lockable="true"
                                       :locked="true">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'position'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion"
                                       :title="$t('person.position')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'positionEn'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion"
                                       :title="$t('person.position')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'email'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :title="$t('email')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'status'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion"
                                       :title="$t('registers.lectors.status')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'statusEn'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion"
                                       :title="$t('registers.lectors.status')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'thematicArea'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion"
                                       :title="$t('registers.lectors.thematicArea')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'thematicAreaEn'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion"
                                       :title="$t('registers.lectors.thematicArea')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionName'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion"
                                       :title="$t('institution.name')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionNameEn'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion"
                                       :title="$t('institution.name')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'phone'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="false"
                                       :title="$t('person.phone')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'mobilePhone'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidde="false"
                                       :title="$t('person.mobilePhone')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionCity'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion"
                                       :title="$t('address.settlement')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionCityEn'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion"
                                       :title="$t('address.settlement')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionCountry'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion"
                                       :title="$t('address.country')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'institutionCountryEn'"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion"
                                       :title="$t('address.country')">
                    </kendo-grid-column>

                </kendo-grid>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import JSZip from 'jszip'
    import store from '../../../store/store';
    import GridUserSettings from "../../../components/grid/UserSettings";
    import BtnsTemplate from "../../../components/grid/StandardBtnsTemplate";
    import PageTitle from '../../../components/common/PageTitle';
    import { KendoGridPageSize } from '../../../models/enums';
    import personalizationService from '../../../services/personalization.service';
    import { mapGetters } from 'vuex';

    /*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
    kendo.pdf.defineFont({
        "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
        "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
        "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
    });


    export default {
        props: {
            showHideButton: {
                default: false,
                type: Boolean
            },
            showPinButton: {
                default: true,
                type: Boolean
            },
            showNewButton: {
                default: true,
                type: Boolean
            },
            showExportTemplateButton: {
                default: false,
                type: Boolean
            },
            showImportFromExcelButton: {
                default: false,
                type: Boolean
            },
            showResizeIcon: {
                default: false,
                type: Boolean
            },
            size: {
                default: 6,
                type: Number
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            }
        },
        components: {
            'grid-user-settings': GridUserSettings,
            PageTitle
        },
        data() {
            return {
                componentName: "LectorsRegisterGrid",
                titleKey: 'registers.lecturers.title',
                defaultGridRows: this.$store.getters.defaultGridRows,
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                searchQuery: '',
                excludedColumnsForSearch: [],
                schemaModelFields: {
                    id: {
                        name: 'id',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.lectors.id'
                    },
                    name: {
                        name: 'name',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'person.firstName'
                    },
                    fullName: {
                        name: 'fullName',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                    },
                    fullNameEn: {
                        name: 'fullNameEn',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                    },
                    nameTransliterated: {
                        name: 'nameTransliterated',
                        type: 'string',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'person.gridHeaders.nameTransliterated'
                    },
                    position: {
                        name: 'position',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'person.position'
                    },
                    positionEn: {
                        name: 'positionEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'person.position'
                    },
                    email: {
                        name: 'email',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'email'
                    },
                    status: {
                        name: 'status',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.lectors.status'
                    },
                    statusEn: {
                        name: 'statusEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.lectors.status'
                    },
                    thematicArea: {
                        name: 'thematicArea',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.lectors.thematicArea'
                    },
                    thematicAreaEn: {
                        name: 'thematicAreaEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'registers.lectors.thematicArea'
                    },
                    institutionName: {
                        name: 'institutionName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.name'
                    },
                    institutionNameEn: {
                        name: 'institutionNameEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.name'
                    },
                    institutionCity: {
                        name: 'institutionCity',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.name'
                    },
                    institutionCityEn: {
                        name: 'institutionCityEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.name'
                    },
                    institutionCountry: {
                        name: 'institutionCountry',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.name'
                    },
                    institutionCountryEn: {
                        name: 'institutionCountryEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'institution.name'
                    },
                    mobilePhone: {
                        name: 'mobilePhone',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'person.mobilePhone'
                    },
                    phone: {
                        name: 'phone',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true,
                        titleKey: 'person.phone'
                    }
                },
                rawHtml: "",
                transportSettings: {
                    read: {
                        url: `${store.getters.baseUrl}/api/lectors/list`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    editable: {
                        mode: 'popup', /* inline, popup */
                        window: {
                            title: this.$t('person.gridTitle'),
                            animation: true
                        },
                        move: true
                    },
                    excel: {
                        fileName: 'Lecturers.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                    pdf: {
                        fileName: 'Lecturers.pdf',
                        title: this.$t(this.titleKey),
                        subject: this.$t(this.titleKey),
                        creator: this.$store.userName,
                        allPages: false,
                        avoidLinks: true,
                        paperSize: 'A4',
                        margin: { top: '2cm', left: '1cm', right: '1cm', bottom: '1cm' },
                        landscape: true,
                        repeatHeaders: true,
                        scale: 0.6
                    }
                },
                showUserSettingsForm: false,
            }
        },
        methods: {
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            goToCreate(event) {
                this.$router.push('/registers/lectors/create');
            },
            goToEdit(args) {
                this.$router.push('/registers/lectors/edit/' + args.id);
            },
            editInTewTabBtnClick(args) {
                let routeData = this.$router.resolve({ name: 'lectorsEdit', params: { id: args.id } })
                window.open(routeData.href, '_blank')
            },
            goToDetails(args) {
                this.$router.push('/registers/lectors/details/' + args.id);
            },
            detailsInTewTabBtnClick(args) {
                let routeData = this.$router.resolve({ name: 'lectorsDetails', params: { id: args.id } })
                window.open(routeData.href, '_blank')
            },
            pdfExport(args) {
            },
            excelExport(args) {
            },
            goToExportTemplate() {
            },
            selectImportFiles() {
            },
            goToImportFromExcel(args) {
            },
            dataBound(e) {
                // This is needed for grid state save
                if (!this.customSettingsLoaded) {
                    this.loadGridSettings();
                }
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    this.saveState();
                    return kendo.stringify(options);
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        parentComponent: this.$refs.grid,
                        btns: [{
                            name: "edit",
                            text: "",
                            tooltip: this.$t('buttons.edit.tooltipText'),
                            class: 'btn btn-success',
                            icon: "fas fa-edit",
                            show: this.claims.lecturer.update,
                            clickHandler: this.goToEdit
                        },
                        {
                            name: "editInNewTab",
                            text: "",
                            tooltip: this.$t('buttons.editInNewTab.tooltipText'),
                            class: 'btn btn-primary',
                            icon: "fas fa-pencil-alt",
                            show: this.claims.lecturer.update,
                            clickHandler: this.editInTewTabBtnClick
                        },
                        {
                            name: "details",
                            text: "",
                            tooltip: this.$t('buttons.details.tooltipText'),
                            class: 'btn btn-info',
                            icon: "fas fa-eye",
                            show: this.claims.lecturer.read,
                            clickHandler: this.goToDetails
                        },
                        {
                            name: "detailsInNewTab",
                            text: "",
                            tooltip: this.$t('buttons.detailsInNewTab.btnText'),
                            class: 'btn btn-primary',
                            icon: "fas fa-eye",
                            show: this.claims.lecturer.read,
                            clickHandler: this.detailsInTewTabBtnClick
                        },
                        {
                            name: "delete",
                            text: "",
                            tooltip: this.$t('buttons.delete.tooltipText'),
                            class: 'btn btn-danger',
                            icon: "fas fa-times",
                            show: this.claims.lecturer.delete,
                            clickHandler: this.deleteBtnClick
                        },
                            //     {
                            //     name: "pdfExport",
                            //     text: "",
                            //     tooltip: this.$t('buttons.pdfExport.tooltipText'),
                            //     class: 'btn btn-danger',
                            //     icon: "far fa-file-pdf",
                            //     show: true,
                            //     clickHandler: this.pdfExport
                            // },
                            // {
                            //     name: "excelExport",
                            //     text: "",
                            //     tooltip: this.$t('buttons.excelExport.tooltipText'),
                            //     class: 'btn btn-success',
                            //     icon: "far fa-file-excel",
                            //     show: true,
                            //     clickHandler: this.excelExport
                            // },
                        ]
                    }
                };
            },
            // This is needed for grid state save and mass search
            getGridOptions() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    return grid.getOptions();
                }
            },
            saveGridSettings() {
                const gridSettings = this.getGridOptions();
                const grid = this.$refs.grid.kendoWidget();
                if (gridSettings && grid) {
                    this.$refs.userSettings.setGridSettings(gridSettings);

                    personalizationService
                        .saveGridSettings(this.componentName, gridSettings)
                        .then(response => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: this.$helpers.errorColor(error),
                                time: this.$store.getters.notificationDuration
                            })
                        });
                }
            },
            loadGridSettings() {
                personalizationService
                    .loadGridSettings(this.componentName)
                    .then(response => {
                        try {
                            var options = personalizationService
                                .mergePersonGridSettings(this.getGridOptions(), response.data.settings);

                            let grid = this.$refs.grid.kendoWidget();
                            if (options && grid) {
                                grid.setOptions(options);

                                if (options.pageable.size !== grid.dataSource.options.pageSize) {
                                    grid.dataSource.pageSize(options.pageable.size);
                                    grid.refresh();
                                }

                                const userSettingsEditor = this.$refs.userSettings;
                                if (userSettingsEditor) {
                                    userSettingsEditor.setGridSettings(grid.getOptions());
                                }
                            }

                            this.customSettingsLoaded = true;
                        } catch (e) {
                            this.customSettingsLoaded = true;
                        }
                    })
                    .catch(error => {
                        this.customSettingsLoaded = true;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            deleteGridSettings() {
                personalizationService
                    .delteGridSettings(this.componentName)
                    .then(response => {
                        this.$router.go();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    try {
                        grid.setOptions(userSettings);
                        if (pageSizeChanged === true) {
                            if (userSettings.pageable.size === KendoGridPageSize.All) {
                                grid.dataSource.pageSize(this.$refs["datasource1"].kendoDataSource.total());
                            } else {
                                grid.dataSource.pageSize(userSettings.pageable.size);
                            }
                            grid.refresh();
                        }
                    } catch (e) {
                    }
                }
            },
            saveState() {
                personalizationService.saveState(this.componentName, this.getGridOptions());
            },
            loadState: function () {
                let stateSettings = personalizationService.getState(this.componentName);
                let options = personalizationService.mergeStateSettings(this.getGridOptions(), stateSettings)
                if (options) {
                    let grid = this.$refs.grid.kendoWidget();
                    if (grid) {
                        grid.setOptions(options);
                    }
                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            refresh() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.read()
                }
            },
            clearFilters() {
                personalizationService.claerState(this.componentName);

                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery, this.excludedColumnsForSearch);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            deleteBtnClick(args) {
                kendo.confirm(`${this.$t('buttons.delete.confirmationText1', { name: this.isEnVersion ? args.fullNameEn : args.fullName })}`)
                    .then(function () {
                        this.deleteLecturer(args.id);
                    }.bind(this));
            },
            deleteLecturer(id) {
                this.$http.delete('/api/lectors/delete',
                    { params: { id: id } }
                ).then((response) => {
                    this.refresh();
                    let statusCode = Number(this.$helpers.responseStatusCode(response));
                    this.$vs.notify({
                        title: this.$t('buttons.delete.action'),
                        text: this.$helpers.responseMessage(response),
                        color: statusCode === 240 ? 'warning' : 'success',
                        time: this.$store.getters.notificationDuration,
                    })
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                })
            },
            // End
        },
        mounted: function () {
            var container = $(this.$el);
            container.find('[data-toggle="tooltip"]').tooltip({ container });
        },
        created: function () {
            window.JSZip = JSZip // Необходимо е за да работи експорта в excel
        },
        computed: {
            token: function () {
                return store.getters.authToken;
            },
            userSettings() {
                let grid = this.$refs.grid.kendoWidget();
                return grid.getOptions();
            },
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }]
            },
            ...mapGetters(['claims', 'isEnVersion'])
        },
        watch: {
            customSettingsLoaded: function (val) {
                if (val === true) {
                    // this.loadState();
                }
            }
        }
    }
</script>

<style scoped>
    .btn {
        margin-right: 10px;
        margin-left: 5px;
    }

    .checkbox {
        margin-right: 5px;
    }

    .selectFile {
        display: none;
    }

    .inlineForm {
        display: inline;
    }

    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: "DejaVu Sans", "Arial", sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit, .k-grid-details, .k-grid-delete, .k-grid-pdf, .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }
</style>
